import { useState } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';

/**
 * Company → Branding card.
 *
 * Every field is an override over the MSP's Portal Settings branding: blank
 * means inherit, and the placeholder shows what will be inherited so an
 * admin can see the fallthrough without opening Portal Settings. "Use MSP
 * branding" deletes the row rather than blanking nine fields one by one.
 *
 * The shape mirrors PaymentSettingsSection on the same page: a status banner
 * with Customize/Edit, an edit form, and a read-only summary.
 */

const FIELDS = [
  { key: 'portalName', label: 'Portal name', placeholder: 'What the portal is called for this client', type: 'text' },
  { key: 'logoUrl', label: 'Logo URL', placeholder: 'https://…/logo.png', type: 'url', hint: 'https only. Shown in the sidebar and in emails.' },
  { key: 'primaryColor', label: 'Primary colour', type: 'color' },
  { key: 'wallpaperUrl', label: 'Wallpaper URL', placeholder: 'https://…/background.jpg', type: 'url', hint: 'https only. Background image for the shell.' },
  { key: 'supportEmail', label: 'Support email', placeholder: 'support@client.example', type: 'email' },
  { key: 'supportPhone', label: 'Support phone', placeholder: '+1 555 0100', type: 'text' },
  { key: 'fromName', label: 'Email from-name', placeholder: 'Sender name on emails to this client', type: 'text', hint: 'The address stays the portal\'s own, so mail still authenticates.' },
  { key: 'locale', label: 'Locale', placeholder: 'en-US', type: 'text', hint: 'BCP 47 tag, e.g. en-AU. Drives date and number formatting.' },
  { key: 'currency', label: 'Currency', placeholder: 'USD', type: 'text', hint: 'ISO 4217 code, e.g. AUD.' },
];

const inputClass = 'w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

const blank = () => Object.fromEntries(FIELDS.map((f) => [f.key, '']));

export default function CompanyBrandingSection({ branding, loading, onSave, onReset }) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(blank());
  const [errors, setErrors] = useState([]);

  const overrides = branding?.overrides || {};
  const msp = branding?.msp || {};
  const effective = branding?.effective || msp;
  const hasOverride = branding ? !branding.usingMspBranding : false;

  const startEdit = () => {
    const next = blank();
    for (const f of FIELDS) next[f.key] = overrides[f.key] ?? '';
    setForm(next);
    setErrors([]);
    setEditMode(true);
  };

  const save = async () => {
    setErrors([]);
    const payload = {};
    for (const f of FIELDS) payload[f.key] = form[f.key]?.trim() ? form[f.key].trim() : null;
    try {
      await onSave(payload);
      setEditMode(false);
    } catch (err) {
      // The API names each bad field; show them rather than a generic failure.
      const list = err.response?.data?.errors;
      setErrors(Array.isArray(list) && list.length ? list : [err.response?.data?.error || 'Failed to save branding']);
    }
  };

  const reset = async () => {
    if (!window.confirm('Remove this company\'s branding and use the portal\'s own? This cannot be undone.')) return;
    setErrors([]);
    try {
      await onReset();
      setEditMode(false);
    } catch (err) {
      setErrors([err.response?.data?.error || 'Failed to reset branding']);
    }
  };

  const field = (f) => {
    const value = form[f.key] ?? '';
    const inherited = msp[f.key] || '';
    return (
      <div key={f.key}>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{f.label}</label>
        {f.type === 'color' ? (
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={/^#[0-9a-f]{6}$/i.test(value) ? value : (inherited || '#f97316')}
              onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
              className="w-12 h-10 rounded border border-neutral-300 dark:border-neutral-600 cursor-pointer"
            />
            <input
              type="text"
              value={value}
              onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
              placeholder={inherited || '#f97316'}
              className={`${inputClass} w-32 font-mono text-sm`}
            />
            <div className="px-4 py-2 rounded-lg text-white font-medium" style={{ backgroundColor: value || inherited || '#f97316' }}>
              Preview
            </div>
            {value && (
              <button type="button" onClick={() => setForm((p) => ({ ...p, [f.key]: '' }))} className="text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200 underline">
                inherit
              </button>
            )}
          </div>
        ) : (
          <input
            type={f.type}
            value={value}
            onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
            placeholder={inherited || f.placeholder}
            className={inputClass}
          />
        )}
        {f.hint && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{f.hint}</p>}
        {!value && inherited && (
          <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">Inherits: {inherited}</p>
        )}
      </div>
    );
  };

  if (editMode) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-700 dark:text-blue-300">
          <p className="font-medium">Customize Branding</p>
          <p className="mt-1">Leave a field blank to inherit the portal's own setting. Logo and wallpaper must be https URLs.</p>
        </div>

        {errors.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <ul className="list-disc pl-4 space-y-0.5">
              {errors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map(field)}
        </div>

        {(form.logoUrl || effective.logoUrl) && (
          <div className="p-3 bg-neutral-900 rounded-lg inline-flex items-center">
            <img src={form.logoUrl || effective.logoUrl} alt="Logo preview" className="max-h-10 max-w-[11rem] object-contain" />
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <button onClick={reset} disabled={loading || !hasOverride} className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 disabled:opacity-50">
            Use MSP branding
          </button>
          <div className="flex gap-2">
            <button onClick={() => setEditMode(false)} disabled={loading} className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg">
              Cancel
            </button>
            <button onClick={save} disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50">
              {loading ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={`flex items-center justify-between p-3 rounded-lg ${
        hasOverride
          ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800'
          : 'bg-neutral-50 dark:bg-neutral-700/50 border border-neutral-200 dark:border-neutral-600'
      }`}>
        <div className="flex items-center gap-2">
          {hasOverride ? (
            <>
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 rounded">Custom Branding</span>
              <span className="text-sm text-neutral-600 dark:text-neutral-300">
                Overrides: {(effective.overridden || []).join(', ')}
              </span>
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm text-neutral-600 dark:text-neutral-300">Using the portal's own branding from Portal Settings</span>
            </>
          )}
        </div>
        <button onClick={startEdit} disabled={!branding} className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium disabled:opacity-50">
          {hasOverride ? 'Edit' : 'Customize'}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Portal name</p>
          <p className="font-medium text-neutral-900 dark:text-white truncate">{effective.portalName || '—'}</p>
        </div>
        <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Primary colour</p>
          <p className="font-medium text-neutral-900 dark:text-white flex items-center gap-2">
            <span className="inline-block w-4 h-4 rounded border border-neutral-300" style={{ backgroundColor: effective.primaryColor }} />
            <span className="font-mono text-sm">{effective.primaryColor}</span>
          </p>
        </div>
        <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Logo</p>
          {effective.logoUrl ? (
            <img src={effective.logoUrl} alt="Logo" className="max-h-8 object-contain" />
          ) : (
            <p className="text-sm text-neutral-500">None</p>
          )}
        </div>
        <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Support</p>
          <p className="text-sm text-neutral-900 dark:text-white truncate">{effective.supportEmail || '—'}</p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">{effective.supportPhone || ''}</p>
        </div>
        <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Email from-name</p>
          <p className="text-sm text-neutral-900 dark:text-white truncate">{effective.fromName || <span className="text-neutral-500">Portal default</span>}</p>
        </div>
        <div className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Locale / currency</p>
          <p className="text-sm text-neutral-900 dark:text-white">{effective.locale} · {effective.currency}</p>
        </div>
      </div>
    </div>
  );
}
